/**
 * Date Range Picker Component
 * Ant Design-style date range picker with two inputs and preset ranges
 */

'use client';

import * as React from 'react';
import { DateRangePicker as ReactDateRangePicker } from 'react-date-range';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { differenceInCalendarDays, format, isAfter, startOfDay } from 'date-fns';
import { cn } from '@/lib/utils';
import { clampRangeToMaxDays } from '@/lib/date-range';
import { useIsMobile } from '@/hooks/use-mobile';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './date-range.css';

interface DateRangePickerProps {
	value?: { startDate?: Date; endDate?: Date };
	onChange?: (range: { startDate?: Date; endDate?: Date }) => void;
	disabled?: (date: Date) => boolean;
	placeholder?: { start?: string; end?: string };
	className?: string;
	/** When true, renders as a normal inline flex element instead of absolute-positioned */
	inline?: boolean;
	/** Accessible name for the button that opens the calendar. */
	ariaLabel?: string;
	/** A calendar icon alone, for a narrow panel: tinted while dates are set, the range shown on hover, a Clear in the calendar. */
	iconOnly?: boolean;
	/** Longest window in days, both ends included. Longer picks keep their end date and move the start in. */
	maxDays?: number;
}

export function DateRangePicker({
	value,
	onChange,
	disabled,
	placeholder = { start: 'Start Date', end: 'End Date' },
	className,
	inline = false,
	ariaLabel = 'Select date range',
	iconOnly = false,
	maxDays,
}: DateRangePickerProps) {
	const isMobile = useIsMobile();
	const [isOpen, setIsOpen] = React.useState(false);
	const [calendarViewState, setCalendarViewState] = React.useState<'initial' | 'updated'>('initial');
	const [dateRange, setDateRange] = React.useState<{
		key: string;
		startDate: Date;
		endDate: Date;
	}>({
		key: 'selection',
		startDate: value?.startDate || new Date(),
		endDate: value?.endDate || new Date()
	});

	// Sync with external value; a cleared value starts the calendar on today again.
	React.useEffect(() => {
		setDateRange({
			key: 'selection',
			startDate: value?.startDate || new Date(),
			endDate: value?.endDate || new Date()
		});
	}, [value?.startDate, value?.endDate]);

	const isDateDisabled = React.useCallback(
		(date: Date) => {
			// Disable future dates
			if (isAfter(startOfDay(date), startOfDay(new Date()))) {
				return true;
			}
			// Once the first date is picked, the second must stay within maxDays of it
			if (maxDays && calendarViewState === 'updated' && Math.abs(differenceInCalendarDays(date, dateRange.startDate)) >= maxDays) {
				return true;
			}
			// Custom disabled function
			if (disabled) {
				return disabled(date);
			}
			return false;
		},
		[disabled, maxDays, calendarViewState, dateRange.startDate]
	);

	const isDateSame = (date1: Date, date2: Date) => date1.toDateString() === date2.toDateString();

	const handleDateSelect = (ranges: any) => {
		const selection = ranges.selection;

		if (!selection) {
			return;
		}

		// Presets already describe a complete range, including a single day like Yesterday.
		const isPresetSelection = Boolean(document.activeElement?.closest('.rdrStaticRange, .rdrInputRanges'));
		if (!isPresetSelection && calendarViewState === 'initial' && isDateSame(selection.startDate, selection.endDate)) {
			// Update state to 'updated' but don't close
			setCalendarViewState('updated');
			setDateRange(selection);
		} else {
			// Second click or different dates - update and close
			const picked = maxDays ? clampRangeToMaxDays(selection.startDate, selection.endDate, maxDays) : selection;
			setDateRange({ ...selection, ...picked });
			onChange?.({
				startDate: picked.startDate,
				endDate: picked.endDate
			});
			// Don't close if the user is typing in one of the "Days up to today" / "Days starting today" inputs
			const isInputRangeFocused = document.activeElement?.classList.contains('rdrInputRangeInput');
			if (!isInputRangeFocused) {
				setCalendarViewState('initial');
				setIsOpen(false);
			}
		}
	};

	const getDisplayText = () => {
		// The applied value, not a half-made pick still in the calendar.
		if (value?.startDate && value?.endDate) {
			// Check if it's a single date (same date for both)
			if (value.startDate.getTime() === value.endDate.getTime()) {
				return format(value.startDate, 'MMM d, yyyy');
			}
			return `${format(value.startDate, 'MMM d, yyyy')} - ${format(value.endDate, 'MMM d, yyyy')}`;
		}
		return placeholder.start && placeholder.end ? `${placeholder.start} - ${placeholder.end}` : 'Select date range';
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (open) {
			// Reset state when opening
			setCalendarViewState('initial');
		}
	};

	const hasValue = value?.startDate && value?.endDate;

	return (
		<Popover open={isOpen} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				{iconOnly ? (
					<button
						type='button'
						aria-label={hasValue ? `${ariaLabel}: ${getDisplayText()}` : ariaLabel}
						title={hasValue ? getDisplayText() : ariaLabel}
						className={cn(
							'flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-900 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2',
							hasValue && 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100',
							className
						)}
					>
						<CalendarIcon className='size-[14px]' />
					</button>
				) : inline ? (
					<button
						type='button'
						aria-label={ariaLabel}
						className={cn('flex w-full cursor-pointer items-center gap-1 rounded-full border-[1.5px] border-gray-300 py-1 pr-1 pl-3 focus-visible:outline-2 focus-visible:outline-offset-2 sm:w-auto', className)}
					>
						<CalendarIcon className='h-4 w-4 shrink-0 text-black-100' />
						<span
							className={cn(
								'flex h-6 min-w-0 flex-1 cursor-pointer items-center justify-center rounded-full bg-gray-100 px-2 text-center text-xs shadow-none hover:bg-gray-200 sm:min-w-[175px]',
								!hasValue && 'text-muted-foreground'
							)}
						>
							{getDisplayText()}
						</span>
					</button>
				) : (
					<button
						type='button'
						aria-label={ariaLabel}
						className={cn(
							'flex flex-1 cursor-pointer items-center justify-end gap-1 rounded-full border-[1.5px] border-gray-300 py-1 pr-1 pl-4 focus-visible:outline-2 focus-visible:outline-offset-2',
							className
						)}
					>
						<CalendarIcon className='h-5 w-5 text-black-100' />
						<span
							className={cn(
								'ml-1.5 flex h-6 min-w-[175px] items-center justify-center rounded-full bg-gray-100 px-2 text-center text-xs shadow-none hover:bg-gray-200',
								!hasValue && 'text-muted-foreground'
							)}
						>
							{getDisplayText()}
						</span>
					</button>
				)}
			</PopoverTrigger>
			<PopoverContent
				// Inline sits flush under its trigger; the other triggers keep their original offset.
				className={cn('relative max-w-[calc(100vw-2rem)] overflow-x-auto rounded-2xl p-0 sm:w-auto', !inline && 'top-6 sm:right-4')}
				align='start'
			>
				<div className='relative'>
					<ReactDateRangePicker
						ranges={[dateRange]}
						onChange={handleDateSelect}
						direction={isMobile ? 'vertical' : 'horizontal'}
						months={isMobile ? 1 : 2}
						moveRangeOnFirstSelection={false}
						disabledDay={isDateDisabled}
						maxDate={new Date()}
						editableDateInputs={true}
					/>
					{iconOnly && hasValue && (
						<div className='flex justify-end border-t border-gray-100 px-3 py-2'>
							<button
								type='button'
								className='cursor-pointer rounded-md px-2 py-1 text-xs text-gray-900 hover:bg-gray-100'
								onClick={() => {
									onChange?.({});
									setIsOpen(false);
								}}
							>
								Any date
							</button>
						</div>
					)}
					{/* <div className="absolute bottom-1 left-0 -right-0.5 flex justify-end border-gray-100 px-4 py-2">
            <Button
              variant="outline"
              className="text-xs rounded-lg"
              size="sm"
              disabled={!hasValue}
              onClick={() => {
                onChange?.({ startDate: undefined, endDate: undefined });
                setDateRange({ key: "selection", startDate: new Date(), endDate: new Date() });
                setCalendarViewState("initial");
              }}
            >
              Clear
            </Button>
          </div> */}
				</div>
			</PopoverContent>
		</Popover>
	);
}
